import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  type Dispatch,
  type SetStateAction,
} from 'react';
import type { Area } from 'react-easy-crop';
import { Upload, ChevronDown, ChevronRight, Image, Crop } from 'lucide-react';
import type { Page, Component } from '../ComponentEditModal.types';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';
import { PageSelector } from '../shared/PageSelector';
import type { ConversionPopupTriggerConfigMode } from '../../../schemas/components/conversion-popup.schemas';
import { ImageCropModal, type CropData } from '../shared/ImageCropModal';
import { getCroppedImageRenderProps, hasCropApplied } from '../shared/cropUtils';
import {
  CONVERSION_POPUP_NO_DIM_OVERLAY,
  isConversionPopupDimEnabled,
} from '../../../utils/conversionPopupOverlay';

const SMART_TIME_DELAY_SECONDS = 15;
const DEFAULT_MIN_DELAY_MS = 5000;
const MAX_MIN_DELAY_SEC = 120;

export type { ConversionPopupTriggerConfigMode };

export interface ConversionPopupFormData {
  triggerConfigMode?: ConversionPopupTriggerConfigMode;
  contentPurpose?: 'offer' | 'lead_magnet' | 'announcement' | 'exit_message';
  headline?: string;
  body?: string;
  primaryCtaLabel?: string;
  primaryCtaUrl?: string;
  primaryCtaTarget?: '_self' | '_blank';
  secondaryCtaLabel?: string;
  contentAlignment?: 'left' | 'center' | 'right';
  imageUrl?: string;
  /** Mirrors form-split/media-text crop payloads; null clears persisted crop on save */
  crop?: CropData | null;
  exitIntentDesktop?: boolean;
  timeDelaySeconds?: number | null;
  scrollDepthPercent?: number | null;
  minDelayAfterLoadMs?: number;
  pageSlugs?: string[];
  showOncePerSession?: boolean;
  excludeAfterConversion?: boolean;
  conversionFormComponentIds?: string[];
  mobileMode?: 'disabled' | 'bottom_sheet' | 'fullscreen';
  maxViewportWidthForMobile?: number;
  priority?: number;
  campaignKey?: string;
  customColors?: Record<string, string | undefined>;
  /** Editor + API: checkbox state for dismiss controls (custom mode) */
  dismissButtonEnabled?: boolean;
}

interface ConversionPopupEditorProps {
  formData: ConversionPopupFormData;
  setFormData: Dispatch<SetStateAction<ConversionPopupFormData>>;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
  onOpenMediaUpload: (mediaType: 'image', assetType: 'image') => void;
}

function applySmartTriggerFields(d: ConversionPopupFormData): ConversionPopupFormData {
  return {
    ...d,
    triggerConfigMode: 'smart',
    exitIntentDesktop: true,
    timeDelaySeconds: SMART_TIME_DELAY_SECONDS,
    scrollDepthPercent: null,
  };
}

function minDelaySeconds(ms: number | undefined): number {
  const raw = Math.round((ms ?? DEFAULT_MIN_DELAY_MS) / 1000);
  return Math.min(MAX_MIN_DELAY_SEC, Math.max(0, raw));
}

type PageScope = 'everywhere' | 'homepage' | 'select';

function getHomeSlug(pages: Page[]): string | null {
  const flagged = pages.find(p => p.isHomePage)?.slug;
  if (flagged) return flagged;
  return pages[0]?.slug ?? null;
}

function derivePageScope(pageSlugs: string[] | undefined, homeSlug: string | null): PageScope {
  const slugs = pageSlugs || [];
  if (slugs.length === 0) return 'everywhere';
  if (homeSlug && slugs.length === 1 && slugs[0] === homeSlug) return 'homepage';
  return 'select';
}

function formComponentsOnPage(components: Component[] | undefined): Component[] {
  if (!components?.length) return [];
  return components.filter((c): c is Component => c.type === 'form' || c.type === 'form-split');
}

export const ConversionPopupEditor: React.FC<ConversionPopupEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  currentPageComponents,
  onOpenMediaUpload,
}) => {
  const data = formData;
  const themeColors = useCustomColors();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [cropAppliedHint, setCropAppliedHint] = useState(false);

  const handleCropApply = useCallback(
    (cropData: CropData, _pixels: Area) => {
      setFormData(prev => ({ ...prev, crop: cropData }));
      setCropAppliedHint(true);
      setIsCropModalOpen(false);
    },
    [setFormData]
  );

  useEffect(() => {
    setCropAppliedHint(false);
  }, [data.imageUrl]);

  const homeSlug = useMemo(() => getHomeSlug(pages), [pages]);
  const pageScope = useMemo(
    () => derivePageScope(data.pageSlugs, homeSlug),
    [data.pageSlugs, homeSlug]
  );

  const mode: ConversionPopupTriggerConfigMode = data.triggerConfigMode ?? 'custom';

  const formOptions = useMemo(
    () => formComponentsOnPage(currentPageComponents),
    [currentPageComponents]
  );

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData(prev => {
      const next = { ...(prev.customColors || {}) } as Record<string, string | undefined>;
      if (value === null || value === undefined || value.trim() === '') {
        delete next[key];
      } else {
        next[key] = value;
      }
      return {
        ...prev,
        customColors: Object.keys(next).length > 0 ? next : undefined,
      };
    });
  };

  const colorFields = getColorFieldsForComponent('conversion-popup', themeColors);
  const appearanceColorFields = colorFields.filter(f =>
    ['overlay', 'panelBackground'].includes(f.key)
  );
  const extendedColorFields = colorFields.filter(
    f => !['overlay', 'panelBackground'].includes(f.key)
  );
  const isDimmed = isConversionPopupDimEnabled(data.customColors?.overlay);

  const setDimBackground = (enabled: boolean) => {
    if (enabled) {
      setFormData(prev => {
        if (prev.customColors?.overlay !== CONVERSION_POPUP_NO_DIM_OVERLAY) {
          return prev;
        }
        const next = { ...(prev.customColors || {}) } as Record<string, string | undefined>;
        delete next.overlay;
        return {
          ...prev,
          customColors: Object.keys(next).length > 0 ? next : undefined,
        };
      });
    } else {
      updateCustomColor('overlay', CONVERSION_POPUP_NO_DIM_OVERLAY);
    }
  };

  const setMode = (next: ConversionPopupTriggerConfigMode) => {
    if (next === 'smart') {
      setFormData(prev => applySmartTriggerFields(prev));
    } else {
      setFormData(prev => ({ ...prev, triggerConfigMode: 'custom' }));
    }
  };

  const setMinDelaySeconds = (sec: number) => {
    const clamped = Math.min(MAX_MIN_DELAY_SEC, Math.max(0, sec));
    setFormData(prev => ({ ...prev, minDelayAfterLoadMs: clamped * 1000 }));
  };

  const setPageScopeChoice = (scope: PageScope) => {
    if (scope === 'everywhere') {
      setFormData(prev => ({ ...prev, pageSlugs: [] }));
      return;
    }
    if (scope === 'homepage') {
      if (homeSlug) setFormData(prev => ({ ...prev, pageSlugs: [homeSlug] }));
      return;
    }
    if (scope === 'select') {
      const cur = data.pageSlugs || [];
      if (cur.length === 0) {
        const seed = homeSlug || pages[0]?.slug;
        setFormData(prev => ({ ...prev, pageSlugs: seed ? [seed] : [] }));
      }
    }
  };

  const togglePageSlug = (slug: string, checked: boolean) => {
    const cur = new Set(data.pageSlugs || []);
    if (checked) cur.add(slug);
    else cur.delete(slug);
    const next = Array.from(cur);
    if (derivePageScope(data.pageSlugs, homeSlug) === 'select' && next.length === 0) {
      return;
    }
    setFormData(prev => ({ ...prev, pageSlugs: next }));
  };

  const toggleFormId = (id: string, checked: boolean) => {
    const cur = new Set(data.conversionFormComponentIds || []);
    if (checked) cur.add(id);
    else cur.delete(id);
    setFormData(prev => ({ ...prev, conversionFormComponentIds: Array.from(cur) }));
  };

  const setRestrictForms = (restrict: boolean) => {
    if (!restrict) {
      setFormData(prev => ({ ...prev, conversionFormComponentIds: [] }));
    } else if (!(data.conversionFormComponentIds || []).length && formOptions.length) {
      setFormData(prev => ({
        ...prev,
        conversionFormComponentIds: [formOptions[0].id],
      }));
    }
  };

  const restrictToSpecificForms = !!(
    data.excludeAfterConversion && (data.conversionFormComponentIds || []).length > 0
  );

  const safetySeconds = minDelaySeconds(data.minDelayAfterLoadMs);

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-4 space-y-3">
        <p className="text-sm font-medium text-gray-900">When should this popup appear?</p>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="radio"
            name="cp-trigger-mode"
            className="mt-1"
            checked={mode === 'smart'}
            onChange={() => setMode('smart')}
          />
          <span>
            <span className="text-sm font-medium text-gray-900">Smart mode (recommended)</span>
            <span className="block text-xs text-gray-600 mt-0.5">
              Shows when a visitor looks like they are leaving the page, or after they have spent{' '}
              {SMART_TIME_DELAY_SECONDS} seconds on the page (whichever comes first).
            </span>
          </span>
        </label>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="radio"
            name="cp-trigger-mode"
            className="mt-1"
            checked={mode === 'custom'}
            onChange={() => setMode('custom')}
          />
          <span>
            <span className="text-sm font-medium text-gray-900">Custom mode</span>
            <span className="block text-xs text-gray-600 mt-0.5">
              Set your own timers, scroll depth, exit intent, and optional button triggers.
            </span>
          </span>
        </label>
      </div>

      {mode === 'custom' && (
        <div className="border border-gray-200 rounded-lg p-4 space-y-4">
          <h3 className="text-md font-medium text-gray-900">Popup triggers</h3>
          <p className="text-xs text-gray-500">
            If you enable more than one, the first condition that fires opens the popup.
          </p>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!data.exitIntentDesktop}
              onChange={e =>
                setFormData(prev => ({ ...prev, exitIntentDesktop: e.target.checked }))
              }
            />
            <span className="text-sm text-gray-700">
              When a user tries to leave the page (desktop only)
            </span>
          </label>
          <div>
            <label className="block text-sm text-gray-700 mb-1">
              After staying on the page for (seconds, leave empty for off)
            </label>
            <input
              type="number"
              min={0}
              max={3600}
              value={
                data.timeDelaySeconds === null || data.timeDelaySeconds === undefined
                  ? ''
                  : data.timeDelaySeconds
              }
              onChange={e => {
                const v = e.target.value;
                if (v === '') setFormData(prev => ({ ...prev, timeDelaySeconds: null }));
                else setFormData(prev => ({ ...prev, timeDelaySeconds: parseInt(v, 10) }));
              }}
              className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">
              After scrolling down (% of page, leave empty for off)
            </label>
            <input
              type="number"
              min={1}
              max={100}
              value={
                data.scrollDepthPercent === null || data.scrollDepthPercent === undefined
                  ? ''
                  : data.scrollDepthPercent
              }
              onChange={e => {
                const v = e.target.value;
                if (v === '') setFormData(prev => ({ ...prev, scrollDepthPercent: null }));
                else setFormData(prev => ({ ...prev, scrollDepthPercent: parseInt(v, 10) }));
              }}
              className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
        </div>
      )}

      {mode === 'custom' && (
        <>
          <div className="border border-gray-200 rounded-lg p-4 space-y-3">
            <h3 className="text-md font-medium text-gray-900">Safety buffer</h3>
            <p className="text-xs text-gray-500">
              Nothing from the triggers above can fire until this much time has passed after the
              page loads.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-sm text-gray-700">Minimum wait after load:</label>
              <input
                type="number"
                min={0}
                max={MAX_MIN_DELAY_SEC}
                value={safetySeconds}
                onChange={e => setMinDelaySeconds(parseInt(e.target.value, 10) || 0)}
                className="w-20 px-2 py-1.5 border border-gray-300 rounded-md"
              />
              <span className="text-sm text-gray-600">seconds</span>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 space-y-3">
            <h3 className="text-md font-medium text-gray-900">Frequency</h3>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={data.showOncePerSession !== false}
                onChange={e =>
                  setFormData(prev => ({ ...prev, showOncePerSession: e.target.checked }))
                }
              />
              <span className="text-sm text-gray-700">Only show once per visitor session</span>
            </label>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 space-y-3">
            <h3 className="text-md font-medium text-gray-900">Where should this show?</h3>
            {isLoadingPages ? (
              <p className="text-sm text-gray-500">Loading pages…</p>
            ) : (
              <>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="cp-page-scope"
                    checked={pageScope === 'everywhere'}
                    onChange={() => setPageScopeChoice('everywhere')}
                  />
                  <span className="text-sm text-gray-700">Everywhere</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="cp-page-scope"
                    disabled={!homeSlug}
                    checked={pageScope === 'homepage'}
                    onChange={() => setPageScopeChoice('homepage')}
                  />
                  <span className="text-sm text-gray-700">
                    Homepage only
                    {!homeSlug && (
                      <span className="text-gray-400"> (set a home page on your site first)</span>
                    )}
                  </span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="cp-page-scope"
                    checked={pageScope === 'select'}
                    onChange={() => setPageScopeChoice('select')}
                  />
                  <span className="text-sm text-gray-700">Select pages…</span>
                </label>
                {pageScope === 'select' && pages.length > 0 && (
                  <div className="ml-6 mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-2">
                    {pages.map(p => (
                      <label key={p.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={(data.pageSlugs || []).includes(p.slug)}
                          onChange={e => togglePageSlug(p.slug, e.target.checked)}
                        />
                        <span className="text-gray-800">{p.title}</span>
                        <span className="text-gray-500 font-mono text-xs">/{p.slug}</span>
                      </label>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="border border-gray-200 rounded-lg p-4 space-y-3">
            <h3 className="text-md font-medium text-gray-900">Smart hiding</h3>
            <p className="text-xs text-gray-500">Stop showing this popup when:</p>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!data.excludeAfterConversion}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    excludeAfterConversion: e.target.checked,
                    ...(e.target.checked ? {} : { conversionFormComponentIds: [] }),
                  }))
                }
              />
              <span className="text-sm text-gray-700">They submitted a form (conversion)</span>
            </label>
            {data.excludeAfterConversion && (
              <div className="ml-6 space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="cp-form-restrict"
                    checked={!restrictToSpecificForms}
                    onChange={() => setRestrictForms(false)}
                  />
                  <span className="text-sm text-gray-700">Any form on the site</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="cp-form-restrict"
                    checked={restrictToSpecificForms}
                    onChange={() => setRestrictForms(true)}
                  />
                  <span className="text-sm text-gray-700">Only these forms (this page)</span>
                </label>
                {restrictToSpecificForms &&
                  (formOptions.length ? (
                    <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-100 rounded p-2">
                      {formOptions.map(fc => {
                        const title = (fc as { data?: { title?: string } }).data?.title || 'Form';
                        const suffix = fc.type === 'form-split' ? ' (split)' : '';
                        return (
                          <label key={fc.id} className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={(data.conversionFormComponentIds || []).includes(fc.id)}
                              onChange={e => toggleFormId(fc.id, e.target.checked)}
                            />
                            <span>
                              {title}
                              {suffix}
                            </span>
                            <span className="text-gray-400 font-mono text-xs truncate">
                              {fc.id}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-amber-700">
                      No form sections on this page — use “Any form” or add a form to this page.
                    </p>
                  ))}
              </div>
            )}
          </div>
        </>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Purpose</label>
        <select
          value={data.contentPurpose || 'offer'}
          onChange={e =>
            setFormData(prev => ({
              ...prev,
              contentPurpose: e.target.value as ConversionPopupFormData['contentPurpose'],
            }))
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="offer">Offer</option>
          <option value="lead_magnet">Lead magnet</option>
          <option value="announcement">Announcement</option>
          <option value="exit_message">Exit message</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Headline *</label>
        <input
          type="text"
          value={data.headline || ''}
          onChange={e => setFormData(prev => ({ ...prev, headline: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          maxLength={200}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Body</label>
        <textarea
          value={data.body || ''}
          onChange={e => setFormData(prev => ({ ...prev, body: e.target.value }))}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          maxLength={2000}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Text &amp; button alignment
        </label>
        <select
          value={data.contentAlignment || 'left'}
          onChange={e =>
            setFormData(prev => ({
              ...prev,
              contentAlignment: e.target.value as ConversionPopupFormData['contentAlignment'],
            }))
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Applies to headline, body, image, and primary button.
        </p>
      </div>

      <div className="border-t pt-6 space-y-4">
        <h3 className="text-md font-medium text-gray-900">Primary CTA</h3>
        <input
          type="text"
          placeholder="Button label"
          value={data.primaryCtaLabel || ''}
          onChange={e => setFormData(prev => ({ ...prev, primaryCtaLabel: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          maxLength={100}
        />
        <PageSelector
          value={data.primaryCtaUrl || ''}
          onChange={url => setFormData(prev => ({ ...prev, primaryCtaUrl: url }))}
          pages={pages}
          isLoadingPages={isLoadingPages}
          currentPageComponents={currentPageComponents}
        />
        <select
          value={data.primaryCtaTarget || '_self'}
          onChange={e =>
            setFormData(prev => ({
              ...prev,
              primaryCtaTarget: e.target.value as '_self' | '_blank',
            }))
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="_self">Same tab</option>
          <option value="_blank">New tab</option>
        </select>
      </div>

      {mode === 'custom' && (
        <div className="border-t pt-6">
          <div className="border border-gray-200 rounded-lg p-4 space-y-4">
            <h3 className="text-md font-medium text-gray-900">Dismiss button</h3>
            <p className="text-xs text-gray-500">
              Optional second button under your main CTA. It only closes the popup—visitors stay on
              the page.
            </p>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5 shrink-0 rounded border-gray-300"
                checked={!!data.dismissButtonEnabled}
                onChange={e => {
                  const on = e.target.checked;
                  if (!on) {
                    setFormData(prev => ({
                      ...prev,
                      dismissButtonEnabled: false,
                      secondaryCtaLabel: '',
                    }));
                  } else {
                    setFormData(prev => ({ ...prev, dismissButtonEnabled: true }));
                  }
                }}
              />
              <span className="text-sm text-gray-700 leading-snug">
                Show a dismiss button below the primary button
              </span>
            </label>
            <div
              className={
                data.dismissButtonEnabled ? 'space-y-1' : 'space-y-1 opacity-50 pointer-events-none'
              }
            >
              <label className="block text-sm text-gray-700 mb-1" htmlFor="cp-dismiss-button-label">
                Dismiss button label
              </label>
              <input
                id="cp-dismiss-button-label"
                type="text"
                placeholder="No thanks, I'll pay full price"
                value={data.secondaryCtaLabel || ''}
                onChange={e =>
                  setFormData(prev => ({ ...prev, secondaryCtaLabel: e.target.value }))
                }
                className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md"
                maxLength={100}
                disabled={!data.dismissButtonEnabled}
              />
              <p className="text-xs text-gray-500">
                The button appears on the site only after you add text here.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="border-t pt-6 space-y-3">
        <h3 className="text-md font-medium text-gray-900">Appearance</h3>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="rounded border-gray-300"
            checked={isDimmed}
            onChange={e => setDimBackground(e.target.checked)}
          />
          <span className="text-sm text-gray-700">Dim page behind popup</span>
        </label>
        <ColorCustomizationPanel
          title=""
          description=""
          customColors={data.customColors as Record<string, string | undefined>}
          colorFields={
            isDimmed
              ? appearanceColorFields
              : appearanceColorFields.filter(f => f.key !== 'overlay')
          }
          onUpdateColor={updateCustomColor}
        />
      </div>

      <div className="border-t pt-6 space-y-4">
        <h3 className="text-md font-medium text-gray-900">Image (optional)</h3>
        <p className="text-sm text-gray-600">
          Upload from your computer or pick from your site library. Crop and grayscale use the same
          controls as Split Form images.
        </p>
        {data.imageUrl ? (
          <>
            <div className="space-y-3">
              <div className="relative border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50 group">
                {(() => {
                  const cropPreview = getCroppedImageRenderProps(data.crop ?? undefined);
                  const useCropBox = Boolean(cropPreview.hasCrop && cropPreview.aspectRatio);
                  return (
                    <div
                      className={`relative w-full overflow-hidden ${useCropBox ? '' : 'h-48'}`}
                      style={useCropBox ? { aspectRatio: cropPreview.aspectRatio } : undefined}
                    >
                      {data.crop?.croppedAreaPercent ? (
                        <img src={data.imageUrl} alt="" style={cropPreview.imageStyle} />
                      ) : (
                        <img
                          src={data.imageUrl}
                          alt=""
                          className="w-full h-full object-cover"
                          style={{
                            filter: data.crop?.grayscale ? 'grayscale(100%)' : undefined,
                          }}
                        />
                      )}
                      {hasCropApplied(data.crop ?? undefined) ? (
                        <div className="absolute top-2 right-2 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center shadow-sm z-10">
                          <Crop size={14} className="text-white" />
                        </div>
                      ) : null}
                    </div>
                  );
                })()}

                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto">
                  <div className="flex flex-wrap gap-3 justify-center px-2">
                    <button
                      type="button"
                      onClick={() => setIsCropModalOpen(true)}
                      className="px-4 py-2 bg-white text-gray-700 rounded-lg shadow-lg hover:bg-gray-50 flex items-center gap-2 text-sm font-medium"
                    >
                      <Crop size={16} />
                      Crop
                    </button>
                    <button
                      type="button"
                      onClick={() => onOpenMediaUpload('image', 'image')}
                      className="px-4 py-2 bg-white text-gray-700 rounded-lg shadow-lg hover:bg-gray-50 flex items-center gap-2 text-sm font-medium"
                    >
                      <Upload size={16} />
                      Replace
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, imageUrl: '', crop: null }));
                        setCropAppliedHint(false);
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg shadow-lg hover:bg-red-700 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>

              <div className="text-sm text-gray-600 flex flex-wrap items-center gap-x-4 gap-y-2 bg-gray-50 px-3 py-2 rounded-md">
                <span className="flex items-center gap-2">
                  <Image size={16} className="text-blue-600" />
                  <span className="font-medium">Popup image</span>
                </span>
                <button
                  type="button"
                  onClick={() => setIsCropModalOpen(true)}
                  className="text-primary-600 hover:text-primary-700 font-medium hover:underline inline-flex items-center gap-1"
                >
                  <Crop size={14} />
                  Crop / edit
                </button>
                {hasCropApplied(data.crop ?? undefined) ? (
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, crop: null }));
                      setCropAppliedHint(false);
                    }}
                    className="text-gray-600 hover:text-gray-800 font-medium hover:underline"
                  >
                    Reset crop
                  </button>
                ) : null}
              </div>
              {cropAppliedHint ? (
                <p className="text-xs text-gray-500">
                  Crop updated — save the component to keep changes. While this panel is open,
                  Preview popup on the page shows your latest crop.
                </p>
              ) : null}
            </div>

            <ImageCropModal
              isOpen={isCropModalOpen}
              onClose={() => setIsCropModalOpen(false)}
              imageUrl={data.imageUrl}
              initialCrop={data.crop ?? undefined}
              onApply={handleCropApply}
            />
          </>
        ) : (
          <button
            type="button"
            onClick={() => onOpenMediaUpload('image', 'image')}
            className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-md hover:border-primary-500 text-gray-600 hover:text-primary-600 flex items-center justify-center gap-2"
          >
            <Upload size={20} />
            <span>Upload or choose from library</span>
          </button>
        )}
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          type="button"
          className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium text-gray-900 bg-gray-50 hover:bg-gray-100"
          onClick={() => setAdvancedOpen(o => !o)}
        >
          Advanced (mobile, priority, campaign)
          {advancedOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>
        {advancedOpen && (
          <div className="p-4 space-y-4 border-t border-gray-200">
            <div>
              <label className="block text-sm text-gray-700 mb-1">Mobile behavior</label>
              <select
                value={data.mobileMode || 'bottom_sheet'}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    mobileMode: e.target.value as ConversionPopupFormData['mobileMode'],
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="bottom_sheet">Bottom sheet</option>
                <option value="fullscreen">Fullscreen</option>
                <option value="disabled">Disabled on small screens</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">
                Max width treated as mobile (px)
              </label>
              <input
                type="number"
                min={320}
                max={1200}
                value={data.maxViewportWidthForMobile ?? 768}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    maxViewportWidthForMobile: parseInt(e.target.value, 10),
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">
                Priority (higher runs first for auto popups)
              </label>
              <input
                type="number"
                min={0}
                max={1000}
                value={data.priority ?? 0}
                onChange={e =>
                  setFormData(prev => ({ ...prev, priority: parseInt(e.target.value, 10) || 0 }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">
                Campaign key (optional session key)
              </label>
              <input
                type="text"
                value={data.campaignKey || ''}
                // Use '' (not undefined) so clearing this field actually
                // persists on save — the backend deep-merge ignores missing
                // keys, which JSON.stringify drops for undefined values.
                onChange={e => setFormData(prev => ({ ...prev, campaignKey: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                maxLength={64}
              />
            </div>
          </div>
        )}
      </div>

      {mode === 'custom' && extendedColorFields.length > 0 && (
        <ColorCustomizationPanel
          title="More colors"
          description="Headline, body, and button colors. Leave empty to use theme defaults."
          customColors={data.customColors as Record<string, string | undefined>}
          colorFields={extendedColorFields}
          onUpdateColor={updateCustomColor}
        />
      )}
    </div>
  );
};
